'use strict';

const { encrypt, decrypt, checksum, buildCanonicalString } = require('./crypto');
const { resolveGatewayUrl } = require('./environments');

function randomRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // RFC4122-ish fallback for environments without crypto.randomUUID (very old browsers/Node <14.17).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const hasXhr = typeof XMLHttpRequest !== 'undefined';

/**
 * Sends one HTTP request and resolves { status, data, headers }.
 *
 * Uses XMLHttpRequest (not fetch) when available: fetch's Request
 * constructor throws on a GET/HEAD request carrying a body, but
 * fintech-gateway's security pipeline requires an encrypted body on
 * *every* call regardless of HTTP method (even its "no fields" GET
 * operations like aeps/statusCheck or aeps/banks) — mainstream browsers
 * do transmit a GET body via XHR even though it's a spec grey area, so
 * this is the only transport that can reach those endpoints from a browser.
 * Falls back to Node's http/https modules (which have no such
 * restriction) when running outside a browser.
 */
function transport(method, url, headers, bodyStr, timeout) {
  if (hasXhr) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.timeout = timeout;
      Object.keys(headers).forEach((k) => xhr.setRequestHeader(k, headers[k]));
      xhr.onload = () => {
        let data;
        try { data = JSON.parse(xhr.responseText); } catch (e) { data = xhr.responseText; }
        const respHeaders = {};
        xhr.getAllResponseHeaders().trim().split(/[\r\n]+/).forEach((line) => {
          const idx = line.indexOf(':');
          if (idx > -1) respHeaders[line.slice(0, idx).trim().toLowerCase()] = line.slice(idx + 1).trim();
        });
        resolve({ status: xhr.status, data, headers: respHeaders });
      };
      xhr.onerror = () => reject(new Error(`Network error calling ${url}`));
      xhr.ontimeout = () => reject(new Error(`Request timed out calling ${url}`));
      xhr.send(bodyStr);
    });
  }

  // Node fallback — no XMLHttpRequest global.
  const { URL } = require('url');
  const parsed = new URL(url);
  const lib = parsed.protocol === 'https:' ? require('https') : require('http');

  return new Promise((resolve, reject) => {
    const req = lib.request(
      {
        method,
        hostname: parsed.hostname,
        port: parsed.port,
        path: `${parsed.pathname}${parsed.search}`,
        headers: { ...headers, 'Content-Length': Buffer.byteLength(bodyStr || '') },
        timeout,
      },
      (res) => {
        let raw = '';
        res.on('data', (chunk) => { raw += chunk; });
        res.on('end', () => {
          let data;
          try { data = JSON.parse(raw); } catch (e) { data = raw; }
          resolve({ status: res.statusCode, data, headers: res.headers });
        });
      },
    );
    req.on('error', reject);
    req.on('timeout', () => req.destroy(new Error(`Request timed out calling ${url}`)));
    req.end(bodyStr);
  });
}

/**
 * GatewayClient — talks only to fintech-gateway; paymentSystem is never
 * addressed directly. Every call, including the "raw" ones vendor/wallet/
 * commission modules use, goes through the gateway's generic wildcard proxy:
 * POST/GET {gatewayUrl}/process/<upstreamPath> -> paymentSystem's
 * <upstreamPath> at the same sub-path, with the standard encrypted envelope
 * (AES-256-GCM body + HMAC-SHA256 checksum over
 * "<timestamp>.<clientId>.<encrypted body JSON>"). The gateway is the only
 * thing that knows paymentSystem's own URL.
 *
 * clientId + key (paymentSystem's User.apiKey / User.SaltAESKey, issued at
 * merchant registration) are the only credential this SDK ever uses — no
 * separate JWT/session token. The gateway forwards every call to
 * paymentSystem with X-Api-Key: clientId, which paymentSystem's own auth
 * hook resolves identity from exactly like any other apiKey call.
 *
 * Naming note: `clientId` and `key` are two *different* paymentSystem
 * fields — clientId is User.apiKey (the identity/lookup key, sent as
 * X-Client-Id), while this config's `key` is actually User.SaltAESKey (the
 * AES-256-GCM encryption key, used internally — and, by default, as the
 * checksum secret too). Don't confuse the two: clientId identifies *who* is
 * calling; key is *what encrypts* the call.
 */
class GatewayClient {
  /**
   * @param {object} config
   * @param {string} [config.gatewayUrl]     - explicit gateway URL — takes priority over `environment` when
   *                                            both are given. Use this for local dev or any URL outside the
   *                                            named environments below.
   * @param {string} [config.environment]    - 'production' | 'staging' — resolves to the matching gateway URL
   *                                            internally instead of hardcoding one. Ignored if gatewayUrl is
   *                                            also set. One of gatewayUrl/environment is required.
   * @param {string} config.clientId         - X-Client-Id header value (paymentSystem's User.apiKey)
   * @param {string} config.key              - 32-byte hex or base64 AES key (paymentSystem's User.SaltAESKey) —
   *                                            used internally for AES-256-GCM encryption (and, by default, the
   *                                            HMAC checksum too). `apiKey` and `aesKey` are both still accepted
   *                                            as deprecated aliases for this same field (in that priority order
   *                                            if more than one is somehow passed).
   * @param {string} [config.checksumSecret] - HMAC secret for X-Checksum. Defaults to key — paymentSystem's
   *                                            live key provider only ever issues one secret (SaltAESKey) per
   *                                            account, reused for both, so this never needs to be passed
   *                                            separately. Only pass a different value if your gateway is
   *                                            configured with a key provider that genuinely uses two secrets
   *                                            (e.g. KEY_PROVIDER=env's merchants.json, which has separate
   *                                            aesKey/checksumSecret fields).
   * @param {string} [config.merchantId]     - optional — only needed for a *vendor* clientId acting on behalf
   *                                            of one merchant. A vendor's apiKey resolves req.vendorId but not
   *                                            req.merchantId server-side (vendors manage many merchants), so
   *                                            paymentSystem's controllers fall back to a merchantId in the
   *                                            request body (see e.g. dmt.bc.controller.js#check_merchant:
   *                                            `req.user?.merchantId || req.body.merchantId`). Automatically
   *                                            merged into every call's body when set; a merchantId already
   *                                            present in a given call's own body always wins. Leave unset for
   *                                            a merchant clientId — nothing is added to the body.
   * @param {number} [config.timeout=30000]  - request timeout ms
   */
  constructor(config) {
    this.gatewayUrl    = resolveGatewayUrl(config).replace(/\/$/, '');
    this.clientId       = config.clientId;
    // `key` is the current public param name; `apiKey`/`aesKey` are kept as
    // deprecated aliases so nothing that already worked stops working.
    // Whichever name it arrives under, this is the actual AES-256-GCM key
    // used to encrypt every request — getting the right value here is what
    // lets the gateway decrypt/validate the call at all, so a bad or
    // missing value fails every transaction.
    this.aesKey          = config.key || config.apiKey || config.aesKey;
    this.checksumSecret = config.checksumSecret || this.aesKey;
    this.merchantId      = config.merchantId;
    this.timeout         = config.timeout || 30_000;
  }

  // ── Internal: gateway call (full crypto pipeline) ─────────────────

  /** @param {string} upstreamPath - paymentSystem sub-path, e.g. "aeps/withdrawal" */
  async _gateway(method, upstreamPath, body = {}) {
    // Only added when configured (a non-empty value), and never overrides a
    // merchantId the caller already put in this specific call's body.
    if (this.merchantId && body.merchantId == null) {
      body = { ...body, merchantId: this.merchantId };
    }

    const timestamp = Math.floor(Date.now() / 1000);
    const requestId = randomRequestId();
    const aad       = `${this.clientId}.${timestamp}`;

    const { payload, iv, tag } = await encrypt(body, this.aesKey, aad);
    const requestBody = { payload };
    const rawBody = JSON.stringify(requestBody);
    const cs = await checksum(timestamp, this.clientId, rawBody, this.checksumSecret);

    const headers = {
      'Content-Type': 'application/json',
      'X-Client-Id':  this.clientId,
      'X-Timestamp':  String(timestamp),
      'X-IV':         iv,
      'X-Tag':        tag,
      'X-Checksum':   cs,
      'X-Request-Id': requestId,
    };

    // Browsers can't reliably deliver a body on a GET request (unlike
    // headers, which have no such restriction) — carry the same encrypted
    // envelope as a header too, so the gateway's normalizeGetBody middleware
    // can recover it when the real body came through empty. Harmless no-op
    // for POST or for transports (Node) that send the GET body just fine.
    if (method === 'GET') headers['X-Body'] = rawBody;

    const path = upstreamPath.replace(/^\//, '');
    const url = `${this.gatewayUrl}/process/${path}`;

    const res = await transport(method, url, headers, rawBody, this.timeout);

    if (res.status >= 400) {
      const err = new Error(res.data?.message || `Request failed with status ${res.status}`);
      err.status = res.status;
      err.response = res.data;
      throw err;
    }

    const respIv  = res.headers['x-iv'];
    const respTag = res.headers['x-tag'];
    const respTs  = res.headers['x-timestamp'];

    // Unencrypted response (e.g. idempotent replay or an edge-case error shape).
    if (!respIv || !respTag || !res.data?.payload) return res.data;

    const respAad = `${this.clientId}.${respTs}`;
    return decrypt(res.data.payload, respIv, respTag, this.aesKey, respAad);
  }

  // ── Public helpers ────────────────────────────────────────────────

  post(upstreamPath, body)     { return this._gateway('POST', upstreamPath, body); }
  get(upstreamPath, body = {}) { return this._gateway('GET',  upstreamPath, body); }
  put(upstreamPath, body)      { return this._gateway('PUT',  upstreamPath, body); }

  /**
   * @deprecated kept only so existing module code reading like a "direct,
   * unencrypted" call keeps working — it's now just an alias for post/get.
   * Every paymentSystem path is reachable through the gateway proxy, so
   * there's no more a separate unencrypted transport to fall back to.
   */
  rawPost(path, body) { return this.post(path, body); }
  rawGet(path)        { return this.get(path); }
}

module.exports = { GatewayClient, buildCanonicalString };
